import throttle from 'lodash.throttle';
import React, { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

import { getMenuDivider, MenuDividerHookValue } from './get-menu-divider';
import { historyMenu } from './history-menu/history-menu';
import { HistoryMenuHookValue } from './history-menu/history-menu-hook';
import { NodeAttachementHookValue, nodeAttachementMenu } from './node-attachement-menu';
import { NodeCutCopyPasteHookValue, nodeCutCopyPasteMenu } from './node-copy-cut-paste-menu';
import { NodeManipulationHookValue, nodeManipulationMenu } from './node-manipulation-menu';

export interface MenuVariantElementProps {
  hook: MenuHook;
}

export type MenuVariant = {
  Element: (props: MenuVariantElementProps) => JSX.Element;
  priority: number;
  fullHeight?: boolean;
};

export type MenuHookValues =
  | NodeManipulationHookValue
  | NodeCutCopyPasteHookValue
  | MenuDividerHookValue
  | HistoryMenuHookValue
  | NodeAttachementHookValue;
export type MenuHook = () => MenuHookValues;

export type Menu = {
  id: string;
  variants: MenuVariant[];
  currentVariant: number;
  hook: MenuHook;
  tempHook: MenuHook;
};

export const useToolBar = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const isTablet = useMediaQuery({ query: '(max-width: 767px)' });
  const widthOffset = -(isTablet ? 24 + 16 : 48 + 24);
  const [sizeDownAgainCount, setSizeDownAgainCount] = useState<number>(0);
  const [toolbar, setToolbar] = useState<Menu[]>([
    historyMenu,
    getMenuDivider(1),
    nodeAttachementMenu,
    getMenuDivider(2),
    nodeManipulationMenu,
    getMenuDivider(3),
    nodeCutCopyPasteMenu,
  ]);

  const isInViewport = () => {
    if (ref.current) return ref.current.getBoundingClientRect().width - widthOffset < window.innerWidth;
  };

  useEffect(() => {
    // check if a size down is neccessary
    if (!isInViewport()) {
      // determine the menu that should use a smaller variant of it by choosing
      // the lowest prioritized variant of all sized down current variants
      const sizeDownableMenuVariants = toolbar.filter(({ currentVariant }) => currentVariant > 0);
      if (sizeDownableMenuVariants.length > 0) {
        const menuToSizeDown = sizeDownableMenuVariants.reduce((prev, current) =>
          prev.variants[prev.currentVariant].priority < current.variants[current.currentVariant].priority
            ? prev
            : current,
        );

        // only size down if there is a down-sizeable menu variant
        if (menuToSizeDown) {
          // size down menu
          setToolbar((menues) =>
            menues.map((menu) =>
              menu.id == menuToSizeDown.id ? { ...menu, currentVariant: menu.currentVariant - 1 } : menu,
            ),
          );

          // try to size down again after a rerender because the
          // current size down might not have been sufficient
          setSizeDownAgainCount((sizeDownAgainCount) => sizeDownAgainCount + 1);
        }
      }
    }
  }, [sizeDownAgainCount, ref.current]);

  const [tempMenu, setTempMenu] = useState<Menu | undefined>();
  const tempMenuCurrentRef = useRef<HTMLDivElement | null>(null);
  const tempMenuSizedUpRef = useRef<HTMLDivElement | null>(null);

  const startSizeUp = () => {
    // determine the menu that should use a larger variant of it by choosing
    // the highest prioritized variant of all sized up current variants
    const sizeUppableMenuVariants = toolbar.filter(
      ({ currentVariant, variants }) => currentVariant < variants.length - 1,
    );
    if (sizeUppableMenuVariants.length > 0) {
      const menuToSizeUp = sizeUppableMenuVariants.reduce((prev, current) =>
        prev.variants[prev.currentVariant + 1].priority >= current.variants[current.currentVariant + 1].priority
          ? prev
          : current,
      );

      // get the menu's size by temporary rendering it
      if (menuToSizeUp) setTempMenu(menuToSizeUp);
    }
  };

  useEffect(() => {
    if (tempMenu && ref.current && tempMenuSizedUpRef.current && tempMenuCurrentRef.current) {
      // check if the toolbar would be still be completely in the
      // viewport with the larger menu from tempMenu
      const inViewport =
        ref.current.getBoundingClientRect().width -
          widthOffset +
          tempMenuSizedUpRef.current.getBoundingClientRect().width -
          tempMenuCurrentRef.current.getBoundingClientRect().width <
        window.innerWidth;
      if (inViewport) {
        // use the larger menu
        setToolbar((menues) =>
          menues.map((menu) => (menu.id == tempMenu.id ? { ...menu, currentVariant: menu.currentVariant + 1 } : menu)),
        );
        // try to size up again after a rerender because the
        // panel might still have place for more size up's
        startSizeUp();
      } else {
        // don't use the larger menu
        setTempMenu(undefined);
      }
    }
  }, [tempMenu]);

  const onResize = throttle(() => {
    if (isInViewport()) startSizeUp();
    else setSizeDownAgainCount(sizeDownAgainCount + 1);
  }, 100);

  useEffect(() => {
    window.addEventListener('resize', onResize, true);
    return () => window.removeEventListener('resize', onResize, true);
  });

  return {
    ref,
    toolbar,
    tempMenu,
    tempMenuSizedUpRef,
    tempMenuCurrentRef,
  };
};
